import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  GitBranch,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  Archive,
  RefreshCw,
  Save,
  X,
  ArrowRight,
  Target,
  AlertCircle,
  Copy,
  Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
};

const CLASSIFICATION_TYPES = [
  { value: 'structural', label: 'Structural', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'recovery', label: 'Recovery', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'isolated', label: 'Isolated', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'structural_recovery', label: 'Structural + Recovery', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'collection_notice', label: 'Collection Notice', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'round_2', label: 'Round 2 Escalation', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'round_3_cfpb', label: 'Round 3 CFPB', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30' },
];

const SAMPLE_RULES = [
  { 
    id: 'route_001', 
    name: 'Late 3+ → Structural', 
    condition: 'late_count >= 3',
    classification: 'structural',
    method_id: 'method_structural_r1',
    priority: 1,
    status: 'active',
    description: 'Accounts with 3 or more late payments indicate structural damage pattern'
  },
  { 
    id: 'route_002', 
    name: 'Collection/Repo → Recovery', 
    condition: 'has_collection || has_repossession',
    classification: 'recovery',
    method_id: 'method_recovery_r1',
    priority: 2,
    status: 'active',
    description: 'Collection accounts or repossessions route to recovery strategy'
  },
  { 
    id: 'route_003', 
    name: 'Single-bureau derogatory → Isolated', 
    condition: 'reporting_bureau_count == 1 && (late_count <= 2 || account_level_charge_off || past_due > 0)',
    classification: 'isolated',
    method_id: 'method_isolated_r1',
    priority: 3,
    status: 'active',
    description: 'Single-bureau derogatory evidence, including 1–2 lates or account-level charge-off/past-due (no payment-history required)'
  },
  { 
    id: 'route_004', 
    name: 'Structural + Recovery Combo', 
    condition: 'late_count >= 3 && (has_collection || has_repossession)',
    classification: 'structural_recovery',
    method_id: 'method_combo_r1',
    priority: 0,
    status: 'active',
    description: 'Both structural damage and recovery items present'
  },
  { 
    id: 'route_005', 
    name: 'Collection Notice Uploaded', 
    condition: 'document_type == "collection_notice"',
    classification: 'collection_notice',
    method_id: 'method_collection_markup',
    priority: 1,
    status: 'active',
    description: 'Route collection notice documents to markup workflow'
  },
  { 
    id: 'route_006', 
    name: 'Round 2 Unresolved', 
    condition: 'round == 2 && prior_dispute_unresolved',
    classification: 'round_2',
    method_id: 'method_round2_escalation',
    priority: 1,
    status: 'draft',
    description: 'Prior dispute went unresolved, escalate in round 2'
  },
  { 
    id: 'route_007', 
    name: 'Round 3 CFPB Escalation', 
    condition: 'round == 3 && escalation_needed',
    classification: 'round_3_cfpb',
    method_id: 'method_cfpb_enforcement',
    priority: 1,
    status: 'draft',
    description: 'Final escalation to CFPB enforcement method'
  },
];

const RuleCard = ({ rule, onEdit, onDelete, onDuplicate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const classification = CLASSIFICATION_TYPES.find(c => c.value === rule.classification);

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group"
      data-testid={`routing-rule-${rule.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-600/20 flex items-center justify-center">
            <GitBranch className="text-orange-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{rule.name}</h3>
            <p className="text-xs text-slate-500">Priority: {rule.priority}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[rule.status]?.color}`}>
            {STATUS_CONFIG[rule.status]?.label || rule.status}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1">
                <button
                  onClick={() => { onEdit(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => { onDuplicate(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  onClick={() => { onDelete(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Condition */}
      <div className="mb-3">
        <p className="text-xs text-slate-500 mb-1">When:</p>
        <code className="text-xs text-orange-300 bg-stone-800 px-2 py-1 rounded font-mono block overflow-x-auto">
          {rule.condition}
        </code>
      </div>

      {/* Classification & Method */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-slate-500">Then:</span>
        <ArrowRight size={12} className="text-slate-600" />
        <span className={`px-2 py-1 text-xs rounded-full border ${classification?.color || 'bg-slate-700 text-slate-300'}`}>
          {classification?.label || rule.classification}
        </span>
      </div>

      {rule.description && (
        <p className="text-xs text-slate-500 mt-2 line-clamp-2">{rule.description}</p>
      )}

      <div className="flex items-center justify-between text-xs text-slate-500 mt-3 pt-3 border-t border-stone-800">
        <span className="font-mono text-amber-400">{rule.method_id}</span>
        <span className="flex items-center gap-1">
          <Target size={12} />
          Classification Rule
        </span>
      </div>
    </div>
  );
};

const RoutingRulesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    condition: '',
    classification: 'structural',
    method_id: '',
    description: '',
    priority: 1,
    status: 'draft',
  });

  // Fetch rules from API
  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rules-methods/routing-rules`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch routing rules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const filteredRules = rules.filter(rule => {
    if (searchQuery && !rule.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (classFilter !== 'all' && rule.classification !== classFilter) return false;
    if (statusFilter !== 'all' && rule.status !== statusFilter) return false;
    return true;
  }).sort((a, b) => a.priority - b.priority);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.condition.trim()) {
      toast.error('Name and condition are required');
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API}/rules-methods/routing-rules/${editingRule.id}`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success('Routing rule updated');
      } else {
        await axios.post(`${API}/rules-methods/routing-rules`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success('Routing rule created');
      }

      setShowCreateModal(false);
      setEditingRule(null);
      setFormData({ name: '', condition: '', classification: 'structural', method_id: '', description: '', priority: 1, status: 'draft' });
      fetchRules();
    } catch (error) {
      toast.error('Failed to save rule');
      console.error(error);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      condition: rule.condition,
      classification: rule.classification,
      method_id: rule.method_id,
      description: rule.description || '',
      priority: rule.priority,
      status: rule.status,
    });
    setShowCreateModal(true);
  };

  const handleDuplicate = async (rule) => {
    try {
      await axios.post(`${API}/rules-methods/routing-rules`, {
        ...rule,
        name: `${rule.name} (Copy)`,
        status: 'draft',
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule duplicated');
      fetchRules();
    } catch (error) {
      toast.error('Failed to duplicate rule');
    }
  };

  const handleDelete = async (rule) => {
    try {
      await axios.delete(`${API}/rules-methods/routing-rules/${rule.id}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <div className="space-y-6" data-testid="routing-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <GitBranch className="text-orange-400" />
            Routing Rules
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Define how extracted signals are classified and routed to methods
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-orange-600 hover:bg-orange-700">
          <Plus size={16} className="mr-2" />
          New Rule
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Zap className="text-orange-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-orange-300">Routing Rules Purpose</h3>
            <p className="text-sm text-orange-200/70 mt-1">
              These rules define <strong>when to apply</strong> a specific strategy. They take extracted signals 
              and classify accounts into categories like Structural, Recovery, Isolated, etc. 
              Each classification links to a specific <strong>Method</strong> in the Method Library.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-stone-900 border-slate-700 text-white"
          />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-[180px] bg-stone-900 border-slate-700 text-white">
            <SelectValue placeholder="Classification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classifications</SelectItem>
            {CLASSIFICATION_TYPES.map(ct => (
              <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-stone-900 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Classification Legend */}
      <div className="flex flex-wrap gap-2">
        {CLASSIFICATION_TYPES.map(ct => (
          <span 
            key={ct.value} 
            className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer hover:opacity-80 transition-opacity ${ct.color}`}
            onClick={() => setClassFilter(classFilter === ct.value ? 'all' : ct.value)}
          >
            {ct.label}
          </span>
        ))}
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-12 bg-stone-900 rounded-xl border border-stone-800">
          <AlertCircle size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white">No rules found</h3>
          <p className="text-slate-400">Try adjusting your filters or create a new rule</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setEditingRule(null);
          setFormData({ name: '', condition: '', classification: 'structural', method_id: '', description: '', priority: 1, status: 'draft' });
        }
      }}>
        <DialogContent className="bg-stone-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Routing Rule' : 'Create Routing Rule'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Define when this classification should be applied
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Rule Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Late 3+ → Structural"
                className="bg-stone-800 border-slate-700"
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Condition (When)</label>
              <Textarea
                value={formData.condition}
                onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                placeholder='e.g., late_count >= 3'
                className="bg-stone-800 border-slate-700 font-mono text-sm"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Classification (Then)</label>
                <Select 
                  value={formData.classification} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, classification: v }))}
                >
                  <SelectTrigger className="bg-stone-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATION_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Priority</label>
                <Input
                  type="number"
                  min={0}
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="bg-stone-800 border-slate-700"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Method ID (links to Method Library)</label>
              <Input
                value={formData.method_id}
                onChange={(e) => setFormData(prev => ({ ...prev, method_id: e.target.value }))}
                placeholder="e.g., method_structural_r1"
                className="bg-stone-800 border-slate-700 font-mono"
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Explain when this rule applies..."
                className="bg-stone-800 border-slate-700"
                rows={2}
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Status</label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-stone-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700">
              <Save size={16} className="mr-2" />
              {editingRule ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoutingRulesPage;
